import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import { Zap, ArrowUpRight, Clock, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_INTERVAL_MS = 60_000; // refresh every minute
const PAGE_SIZE = 12; // how many alerts to show per "page"

const formatRelativeDate = (iso, language) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const opts = { day: '2-digit', month: 'short', year: 'numeric' };
    return d.toLocaleDateString(language === 'nl' ? 'nl-BE' : 'en-GB', opts);
  } catch {
    return null;
  }
};

const ActionAlertsPage = () => {
  const { t, language } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Prefer the translated text for the visitor's language, fall back
  // gracefully to the other translation or the original source text.
  const displayText = (alert) => {
    if (language === 'nl') return alert.action_text_nl || alert.action_text_en || alert.action_text;
    return alert.action_text_en || alert.action_text_nl || alert.action_text;
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/action-alerts`);
      setAlerts(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (err) {
      setError(t('Acties konden niet geladen worden.', 'Could not load alerts.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="action-alerts-page">
      <SEO
        title={t('Action Alerts | Live partner-acties', 'Action Alerts | Live partner promotions')}
        description={t(
          'Bekijk live actuele acties, kortingen en promoties van onze partner-merken. Realtime bijgewerkt.',
          'Browse live current actions, discounts and promotions from our partner brands. Updated in real-time.'
        )}
        canonical={
          typeof window !== 'undefined' ? window.location.origin + '/acties' : 'https://uandfashion.eu/acties'
        }
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-pink-50/60 via-purple-50/40 to-indigo-50/40 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-100 text-pink-700 text-xs font-medium mb-5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse" aria-hidden="true" />
            {t('ACTUEEL', 'CURRENT')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-gray-900 leading-[1.1] tracking-tight mb-6">
            {t('Action Alerts', 'Action Alerts')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-light max-w-2xl mx-auto">
            {t(
              'Alle actuele acties, kortingen en promoties van onze partner-merken op één plek. De nieuwste staat bovenaan.',
              'All current actions, discounts and promotions from our partner brands in one place. Newest on top.'
            )}
          </p>
        </div>
      </section>

      {/* List */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        {loading ? (
          <div className="text-center text-gray-500 py-12" data-testid="action-alerts-loading">
            {t('Acties aan het laden…', 'Loading alerts…')}
          </div>
        ) : error ? (
          <div className="text-center text-red-600 py-12">{error}</div>
        ) : alerts.length === 0 ? (
          <div
            className="bg-gradient-to-br from-pink-50 via-purple-50/40 to-indigo-50/40 border border-purple-100 rounded-2xl px-6 sm:px-10 py-14 text-center"
            data-testid="action-alerts-empty"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/70 backdrop-blur-sm mb-5 shadow-sm">
              <Zap className="w-6 h-6 text-purple-500" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-light text-gray-900 mb-3 tracking-tight">
              {t(
                'Onze redactie zit klaar voor de volgende actie',
                "Our editors are watching for the next promotion"
              )}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto leading-relaxed mb-8">
              {t(
                'We houden alle promoties van onze partner-merken in de gaten. Zodra een nieuwe sale, kortingsperiode of exclusieve aanbieding live gaat, verschijnt ze hier.',
                'We keep an eye on all promotions from our partner brands. As soon as a new sale, discount or exclusive offer goes live, it will appear here.'
              )}
            </p>
            <p className="text-sm text-gray-700 mb-4 font-medium">
              {t('Wil je geen enkele actie missen?', 'Want to never miss an offer?')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              data-testid="action-alerts-empty-newsletter-cta"
            >
              <Mail className="w-4 h-4" />
              {t('Schrijf je in op de nieuwsbrief', 'Subscribe to our newsletter')}
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="action-alerts-list">
            {alerts.slice(0, visibleCount).map((alert) => {
              const validUntil = formatRelativeDate(alert.valid_until, language);
              const createdAt = formatRelativeDate(alert.created_at, language);
              const hasBanner = Boolean(alert.image_url);
              return (
                <a
                  key={alert.id}
                  href={alert.affiliate_link}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="group block bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-pink-200 hover:shadow-md transition-all"
                  data-testid={`action-alert-${alert.id}`}
                >
                  {hasBanner && (
                    <div className="w-full bg-gray-50 overflow-hidden">
                      <img
                        src={alert.image_url}
                        alt={`${alert.brand_name} — ${displayText(alert)}`}
                        className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-5 p-6 sm:p-7">
                    {!hasBanner && (
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-purple-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1.5">
                        <p className="text-xs uppercase tracking-[0.25em] text-pink-500 font-medium">
                          {alert.brand_name}
                        </p>
                        {createdAt && (
                          <span className="text-xs text-gray-400">· {createdAt}</span>
                        )}
                      </div>
                      <h3 className="text-xl md:text-2xl font-medium text-gray-900 leading-tight mb-3 group-hover:text-pink-600 transition-colors">
                        {displayText(alert)}
                      </h3>
                      {validUntil && (
                        <div className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                          <Clock className="w-3 h-3" />
                          {t('Geldig tot ', 'Valid until ')}
                          {validUntil}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 self-start">
                      <div className="inline-flex items-center gap-1.5 bg-gray-900 text-white px-4 py-2.5 rounded-full text-sm font-medium group-hover:bg-pink-600 transition-colors">
                        {t('Bekijk', 'View')}
                        <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}

            {alerts.length > visibleCount && (
              <div className="pt-6 flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-pink-600 transition-colors"
                  data-testid="action-alerts-load-more"
                >
                  {t('Laad meer acties', 'Load more alerts')}
                </button>
                <p className="text-xs text-gray-400">
                  {t(
                    `${visibleCount} van ${alerts.length} getoond`,
                    `Showing ${visibleCount} of ${alerts.length}`
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default ActionAlertsPage;
